/**
 * `/login?next=<path>` — where to go after sign-in, so a link opened while
 * signed out (a push tap, a link in a chat) still reaches its record.
 *
 * Import-free on purpose: scripts/mobile-deep-links.test.mjs loads it straight
 * into node.
 */

const PROBE_ORIGIN = "http://next.invalid";

/**
 * The resolved path when `raw` is a same-origin path, else null. The login
 * form pushes it as is, so anything that could leave the site is refused:
 * "//host" and "/\host" are protocol-relative, browsers drop tabs and
 * newlines from URLs ("/\t/host" becomes "//host"), and dot segments resolve
 * the same way ("/.//host", "/a/..//host"), which is why the RESOLVED path is
 * checked and returned, never `raw`. /login itself is refused too, so a
 * sign-in never loops back to the form.
 */
export function safeNextPath(raw: string | null | undefined): string | null {
  if (!raw || raw[0] !== "/" || raw[1] === "/") return null;
  for (let i = 0; i < raw.length; i++) {
    const code = raw.charCodeAt(i);
    // Control characters, DEL and "\" (browsers read "\" as "/").
    if (code < 0x20 || code === 0x7f || code === 0x5c) return null;
  }

  let url: URL;
  try {
    url = new URL(raw, PROBE_ORIGIN);
  } catch {
    return null;
  }
  if (url.origin !== PROBE_ORIGIN) return null;
  const path = url.pathname + url.search + url.hash;
  if (path.startsWith("//")) return null;
  if (url.pathname === "/login" || url.pathname.startsWith("/login/")) return null;
  return path;
}

/** The login URL that returns to `path` (pathname + query) after sign-in. */
export function loginHref(path: string): string {
  const next = safeNextPath(path);
  return next && next !== "/" ? `/login?next=${encodeURIComponent(next)}` : "/login";
}

// Set by AuthGuard once this page load has a signed-in user.
let signedInThisLoad = false;

export function noteSignedIn(): void {
  signedInThisLoad = true;
}

/**
 * Where a page sends a visitor who is not signed in. The page is kept
 * (`?next=`) only when this page load never had a signed-in user: a push tap
 * or a link opened with no token or an old one. A session that ends while in
 * use (sign-out, expiry, a sign-in on another device) goes to a plain /login,
 * so on a shared phone or desk the next person starts from home, as before.
 */
export function signInHref(path: string): string {
  return signedInThisLoad ? "/login" : loginHref(path);
}
